import { api } from './api';

export interface SupportNotification {
  id: string;
  userId: string;
  customerId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  readAt?: string;
  actionUrl?: string;
  conversationId?: string;
  inboxId?: string;
  contactId?: string;
  supportGroupId?: string;
  conversationDisplayId?: string;
  inboxName?: string;
  contactName?: string;
  priority: string;
  createdAt: string;
}

export interface SupportNotificationEvent {
  code: string;
  title: string;
  description: string;
  isEmitted: boolean;
}

export interface ClassificationNotifyRule {
  fieldCode: string;
  slugs: string[];
}

export interface ClassificationNotifyRules {
  anyOf: ClassificationNotifyRule[];
}

export interface SupportExternalChannel {
  id: string;
  customerId: string;
  inboxId: string;
  name: string;
  channel: string;
  enabled: boolean;
  webhookUrlMasked?: string | null;
  recipients: string[];
  eventTypes: string[];
  classificationRules: ClassificationNotifyRules;
  createdAt: string;
  updatedAt: string;
}


export interface CreateSupportExternalChannelPayload {
  inboxId: string;
  name: string;
  channel: 'Discord' | 'Email';
  enabled: boolean;
  webhookUrl?: string;
  recipients?: string[];
  eventTypes?: string[];
  classificationRules?: ClassificationNotifyRules;
}

export interface UpdateSupportExternalChannelPayload {
  name: string;
  enabled: boolean;
  webhookUrl?: string;
  recipients?: string[];
  eventTypes?: string[];
  classificationRules?: ClassificationNotifyRules;
}

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value))
  {
    return [];
  }

  return value.map((item) => String(item));
};

const emptyClassificationRules = (): ClassificationNotifyRules => ({ anyOf: [] });

const normalizeClassificationRules = (raw: unknown): ClassificationNotifyRules => {
  if (!raw || typeof raw !== 'object')
  {
    return emptyClassificationRules();
  }

  const obj = raw as Record<string, unknown>;
  const anyOf = obj.anyOf ?? obj.AnyOf;
  if (!Array.isArray(anyOf))
  {
    return emptyClassificationRules();
  }

  return {
    anyOf: anyOf
      .map((item) => {
        if (!item || typeof item !== 'object')
        {
          return null;
        }

        const rule = item as Record<string, unknown>;
        const fieldCode = String(rule.fieldCode ?? rule.FieldCode ?? '').trim();
        const slugs = asStringArray(rule.slugs ?? rule.Slugs)
          .map((slug) => slug.trim())
          .filter((slug) => slug.length > 0);

        if (!fieldCode || slugs.length === 0)
        {
          return null;
        }

        return { fieldCode, slugs };
      })
      .filter((rule): rule is ClassificationNotifyRule => rule != null),
  };
};

const normalizeChannel = (raw: Record<string, unknown>): SupportExternalChannel => ({
  id: String(raw.id ?? raw.Id ?? ''),
  customerId: String(raw.customerId ?? raw.CustomerId ?? ''),
  inboxId: String(raw.inboxId ?? raw.InboxId ?? ''),
  name: String(raw.name ?? raw.Name ?? ''),
  channel: String(raw.channel ?? raw.Channel ?? ''),
  enabled: Boolean(raw.enabled ?? raw.Enabled),
  webhookUrlMasked: (raw.webhookUrlMasked ?? raw.WebhookUrlMasked ?? null) as string | null,
  recipients: asStringArray(raw.recipients ?? raw.Recipients),
  eventTypes: asStringArray(raw.eventTypes ?? raw.EventTypes),
  classificationRules: normalizeClassificationRules(
    raw.classificationRules ?? raw.ClassificationRules
  ),
  createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
  updatedAt: String(raw.updatedAt ?? raw.UpdatedAt ?? ''),
});

const normalizeEvent = (raw: Record<string, unknown>): SupportNotificationEvent => ({
  code: String(raw.code ?? raw.Code ?? ''),
  title: String(raw.title ?? raw.Title ?? ''),
  description: String(raw.description ?? raw.Description ?? ''),
  isEmitted: Boolean(raw.isEmitted ?? raw.IsEmitted),
});

class SupportNotificationService {
  async getNotifications(params?: {
    inboxId?: string;
    unreadOnly?: boolean;
    skip?: number;
    take?: number;
  }): Promise<SupportNotification[]> {
    const response = await api.get('/support/notifications', { params });
    return response.data;
  }

  async getUnreadCount(inboxId?: string): Promise<number> {
    const notifications = await this.getNotifications({
      unreadOnly: true,
      inboxId,
      take: 1000,
    });
    return notifications.length;
  }

  async markAsRead(id: string): Promise<void> {
    await api.patch(`/support/notifications/${id}/read`);
  }

  async markAllAsRead(inboxId?: string): Promise<void> {
    await api.post('/support/notifications/mark-all-read', { inboxId });
  }

  async getNotificationEvents(): Promise<SupportNotificationEvent[]> {
    const response = await api.get('/support/notification-events');
    return (response.data ?? []).map((raw: Record<string, unknown>) => normalizeEvent(raw));
  }

  async getExternalChannels(inboxId: string): Promise<SupportExternalChannel[]> {
    const response = await api.get('/support/external-channels', { params: { inboxId } });
    return (response.data ?? []).map((raw: Record<string, unknown>) => normalizeChannel(raw));
  }

  async createExternalChannel(
    payload: CreateSupportExternalChannelPayload
  ): Promise<SupportExternalChannel> {
    const response = await api.post('/support/external-channels', payload);
    return normalizeChannel(response.data as Record<string, unknown>);
  }

  async updateExternalChannel(
    id: string,
    payload: UpdateSupportExternalChannelPayload
  ): Promise<SupportExternalChannel> {
    const response = await api.put(`/support/external-channels/${id}`, payload);
    return normalizeChannel(response.data as Record<string, unknown>);
  }

  async deleteExternalChannel(id: string): Promise<void> {
    await api.delete(`/support/external-channels/${id}`);
  }

  async testExternalChannel(id: string): Promise<void> {
    await api.post(`/support/external-channels/${id}/test`);
  }
}

export const supportNotificationService = new SupportNotificationService();
